import { useQuery } from "@apollo/client/react";
import { createContext, type ReactNode, use, useMemo } from "react";
import { GetCurrentTeamDocument, type TeamFragment, type UserFragment } from "../gql/generated/graphqlSchema.tsx";

type CurrentSessionValue = {
  readonly user: UserFragment;
  readonly team: TeamFragment | null;
  readonly refetchUser: () => Promise<unknown>;
  readonly refetchTeam: () => Promise<unknown>;
};

// Session data shared below the auth gate: the user comes from
// AuthenticatedRoutes' gate query (fetched once, passed down), the team is
// fetched once here — under the no-cache Apollo client every other
// useQuery consumer would fire its own network request for values that are
// static within a session. Call refetchUser / refetchTeam after mutating them.
const CurrentSessionContext = createContext<CurrentSessionValue | null>(null);

export const CurrentSessionProvider = ({
  user,
  refetchUser,
  children,
}: {
  readonly user: UserFragment;
  readonly refetchUser: () => Promise<unknown>;
  readonly children: ReactNode;
}) => {
  const { data, previousData, refetch } = useQuery(GetCurrentTeamDocument);
  const team = (data ?? previousData)?.currentTeam ?? null;
  const value = useMemo(() => ({ user, team, refetchUser, refetchTeam: refetch }), [user, team, refetchUser, refetch]);
  return <CurrentSessionContext value={value}>{children}</CurrentSessionContext>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCurrentSession = (): CurrentSessionValue => {
  const context = use(CurrentSessionContext);
  if (!context) throw new Error("useCurrentSession must be used within a CurrentSessionProvider");
  return context;
};
